import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  addSocialBookmark, removeSocialBookmark, setSocialFeedFeedback,
  toggleSocialReaction, reportSocialContent, deleteSocialPost,
} from "../api";
import styles from "../styles/SocialNetwork.module.css";

const first = (...values) => values.find((v) => v !== undefined && v !== null && v !== "");
export const postIdOf = (p) => first(p?.id, p?.post_id, p?.uuid);
export const postCreatedAt = (p) => first(p?.created_at, p?.published_at, p?.timestamp);
export const postAuthorName = (p) => first(p?.author?.display_name, p?.display_name, p?.author_name, p?.username, p?.author?.username, "Fan");
export const postIdentifier = (p) => first(p?.author?.username, p?.username, p?.author?.id, p?.user_id, "");
export const postTags = (p) => Array.isArray(p?.tags) ? p.tags : Array.isArray(p?.team_tags) ? p.team_tags : [];
const relativeTime = (value) => {
  const seconds = Math.max(1, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`; if (seconds < 3600) return `${Math.floor(seconds/60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds/3600)}h`; return `${Math.floor(seconds/86400)}d`;
};
const RichText = ({ text }) => {
  const parts = String(text || "").split(/([#@][\w.-]+)/g);
  return parts.map((part, index) => {
    if (part.startsWith("#")) return <Link key={index} to={`/search?q=${encodeURIComponent(part.slice(1))}&tab=posts`}>{part}</Link>;
    if (part.startsWith("@")) return <Link key={index} to={`/users/${part.slice(1)}`}>{part}</Link>;
    return part;
  });
};

export default function SocialPostCard({ post, onDelete, onRepost, onChanged }) {
  const navigate = useNavigate();
  const id = postIdOf(post);
  const identifier = postIdentifier(post);
  const author = postAuthorName(post);
  const [liked, setLiked] = useState(!!(post.liked_by_me || post.reacted_by_me));
  const [likes, setLikes] = useState(Number(post.like_count || post.likes || 0));
  const [bookmarked, setBookmarked] = useState(!!post.bookmarked_by_me);
  const [menu, setMenu] = useState(false);
  const tags = postTags(post);
  const body = first(post.body, post.text, post.content, "");
  const context = first(post.room_context?.team_name, tags.find?.((t) => t?.tag_type === "team")?.team_name, tags.find?.((t) => t?.tag_type === "team")?.value);
  const react = async () => {
    const before = liked; setLiked(!before); setLikes((n) => Math.max(0,n + (before ? -1 : 1)));
    try { await toggleSocialReaction({target_type:"user_post",target_id:id,reaction_type:"like"}); }
    catch { setLiked(before); setLikes((n) => Math.max(0,n + (before ? 1 : -1))); }
  };
  const bookmark = async () => {
    const before = bookmarked; setBookmarked(!before);
    try { before ? await removeSocialBookmark("user_post",id) : await addSocialBookmark("user_post",id); }
    catch { setBookmarked(before); }
  };
  const action = async (kind) => {
    setMenu(false);
    if (kind === "less" || kind === "more") { await setSocialFeedFeedback(id, kind); onChanged?.(kind); }
    if (kind === "report") await reportSocialContent({target_type:"user_post",target_id:id,reason:"other"});
    if (kind === "delete") { await deleteSocialPost(id); onDelete?.(id); }
  };
  return <article className={styles.post}>
    {post.author?.avatar_url ? <img className={styles.avatar} loading="lazy" src={post.author.avatar_url} alt="" /> : <span className={styles.avatar}>{author[0]?.toUpperCase()}</span>}
    <div>
      <div className={styles.postHeader}>
        <Link className={styles.name} to={`/users/${identifier}`} onMouseEnter={() => identifier && import("../api").then(({fetchUserProfile}) => fetchUserProfile(identifier).catch(()=>{}))}>{author}</Link>
        {identifier && <span className={styles.meta}>@{identifier}</span>}
        <Link className={styles.meta} to={`/posts/${id}`}>· {postCreatedAt(post) ? relativeTime(postCreatedAt(post)) : ""}</Link>
        <button className={styles.more} onClick={() => setMenu(!menu)} aria-label="Post options">•••</button>
      </div>
      {context && <div className={styles.context}>{context} fan space</div>}
      <div className={styles.body}><RichText text={body} /></div>
      {post.image_url && <img className={styles.postImage} loading="lazy" src={post.image_url} alt="Post attachment" />}
      {!!tags.length && <div className={styles.tags}>{tags.map((tag,index) => <Link className={styles.tag} key={`${tag.value || tag}-${index}`} to={`/search?q=${encodeURIComponent(tag.value || tag.team_name || tag)}&tab=posts`}>{tag.tag_type === "topic" ? "#" : ""}{tag.team_name || tag.value || tag}</Link>)}</div>}
      <div className={styles.actions}>
        <button className={styles.action} onClick={() => navigate(`/posts/${id}`)}>◯ {post.comment_count || 0}</button>
        <button className={styles.action} onClick={() => onRepost?.(post)}>⇄ {post.repost_count || 0}</button>
        <button className={`${styles.action} ${liked ? styles.actionOn : ""}`} onClick={react}>♡ {likes}</button>
        <button className={`${styles.action} ${bookmarked ? styles.actionOn : ""}`} onClick={bookmark}>⌑</button>
        <button className={styles.action} onClick={() => navigator.share ? navigator.share({title:author,url:`${window.location.origin}/posts/${id}`}) : navigator.clipboard?.writeText(`${window.location.origin}/posts/${id}`)}>↗</button>
      </div>
      {menu && <div className={styles.panel} style={{marginTop:8}}>
        <button className={styles.action} onClick={() => action("more")}>Show me more like this</button>
        <button className={styles.action} onClick={() => action("less")}>Not interested</button>
        <button className={styles.action} onClick={() => action("report")}>Report post</button>
        {post.can_delete && <button className={styles.action} onClick={() => action("delete")}>Delete post</button>}
      </div>}
    </div>
  </article>;
}
